import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/shared/Logo";
import { ArrowLeft, Mail, Headset, Building2 } from "lucide-react";

export default function ContactPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <header className="bg-navy-900 border-b border-navy-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <div onClick={() => navigate("/")}><Logo size="md" className="text-white cursor-pointer" /></div>
          <LanguageSelector variant="dark" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <Button variant="ghost" className="mb-4 text-slate-600 font-body" onClick={() => navigate(-1)} data-testid="contact-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t("contact.back")}
        </Button>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-2">
              <Headset className="w-8 h-8 text-gold-500" />
              <div>
                <CardTitle className="font-sans text-2xl text-slate-900">{t("contact.title")}</CardTitle>
                <p className="text-sm text-slate-500 font-body">{t("contact.subtitle")}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 font-body text-slate-700 text-sm leading-relaxed">
            <p>{t("contact.intro")}</p>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Support email */}
              <a href="mailto:support@staffera.eu"
                className="group flex items-start gap-4 p-5 rounded-lg border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all duration-200"
                data-testid="contact-support-card">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center group-hover:bg-sky-200 transition-colors">
                  <Mail className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-sans font-semibold text-base text-slate-900 mb-1">{t("contact.support_title")}</h3>
                  <p className="text-slate-500 text-xs mb-2">{t("contact.support_desc")}</p>
                  <span className="text-sky-600 font-medium text-sm">support@staffera.eu</span>
                </div>
              </a>

              {/* Admin / business email */}
              <a href="mailto:admin@staffera.eu"
                className="group flex items-start gap-4 p-5 rounded-lg border border-slate-200 hover:border-gold-300 hover:shadow-md transition-all duration-200"
                data-testid="contact-admin-card">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                  <Building2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-sans font-semibold text-base text-slate-900 mb-1">{t("contact.admin_title")}</h3>
                  <p className="text-slate-500 text-xs mb-2">{t("contact.admin_desc")}</p>
                  <span className="text-amber-600 font-medium text-sm">admin@staffera.eu</span>
                </div>
              </a>
            </div>

            <div className="pt-4 border-t border-slate-200 text-center">
              <p className="text-xs text-slate-400">{t("contact.footer_note")}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
